import React from 'react';
import { PageSection, SiteData } from '../../../types';
import { Sparkles, ArrowRight, Image as ImageIcon } from 'lucide-react';
import { interpolateBusinessTokens } from '../../../utils/dynamicContent';
import { WebsiteButton } from '../../common/WebsiteButton';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
  isPreview?: boolean;
  onOpenImagePicker?: () => void;
}

export const HeroSection: React.FC<SectionProps> = ({
  section,
  siteData,
  onNavigate,
  isPreview = false,
  onOpenImagePicker,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = interpolateBusinessTokens(
    content.badge || (siteData.industry === 'YACHT_RENTAL' ? 'LÜKS MAVİ YOLCULUK & CHARTER' : 'PROFESYONEL ÇÖZÜMLER'),
    siteData.settings
  );
  const title = interpolateBusinessTokens(
    content.title || (siteData.industry === 'YACHT_RENTAL' ? 'Ege ve Akdeniz’in Sakin Koylarında Rüya Gibi Bir Seyir' : 'İşinizi Geleceğe Taşıyoruz'),
    siteData.settings
  );
  const description = interpolateBusinessTokens(
    content.description ||
      (siteData.industry === 'YACHT_RENTAL'
        ? 'Ultra lüks motoryat, gulet ve katamaran seçeneklerimiz ile kişiye özel rotalarda kusursuz VIP yat kiralama deneyimi yaşayın.'
        : 'Uzman ekibimizle size en uygun çözümleri sunuyor, işinizi bir adım öne taşıyoruz.'),
    siteData.settings
  );

  const buttonText = content.buttonText || (siteData.industry === 'YACHT_RENTAL' ? 'Yatlarımızı İnceleyin' : 'Hizmetlerimizi İncele');
  const buttonAction = content.buttonAction || {
    type: content.buttonActionType || 'section',
    targetSection: '#services',
    targetPage: content.buttonLink || '/hizmetler',
  };

  const secondaryButtonText = content.secondaryButtonText || 'Hakkımızda';
  const secondaryButtonAction = content.secondaryButtonAction || {
    type: 'page',
    targetPage: content.secondaryButtonLink || '/hakkimizda',
  };

  const bgImageUrl =
    content.bgImageUrl ||
    (siteData.industry === 'YACHT_RENTAL'
      ? 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=1600&auto=format&fit=crop'
      : 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop');

  const bgOverlayOpacity = content.bgOverlayOpacity ?? 70;
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  return (
    <section
      id="hero"
      data-section-id={section.id}
      data-section-type="HERO"
      className="relative min-h-[580px] sm:min-h-[640px] flex items-center justify-center text-white overflow-hidden py-24 px-4 sm:px-6 lg:px-8 select-none"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={bgImageUrl}
          alt={title}
          className="w-full h-full object-cover object-center"
          referrerPolicy="no-referrer"
        />
        {/* Color Overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-900/60"
          style={{ opacity: bgOverlayOpacity / 100 }}
        />
      </div>

      {/* In-Editor Image Changer Button */}
      {!isPreview && onOpenImagePicker && (
        <button
          type="button"
          onClick={onOpenImagePicker}
          className="absolute top-4 right-4 z-20 px-3.5 py-1.5 rounded-xl bg-slate-900/90 hover:bg-slate-900 text-white text-xs font-bold border border-slate-700 shadow-xl flex items-center gap-1.5 transition active:scale-95"
        >
          <ImageIcon className="w-3.5 h-3.5 text-blue-400" />
          <span>Görseli Değiştir</span>
        </button>
      )}

      {/* Content Container */}
      <div className="relative z-10 max-w-4xl mx-auto text-center space-y-6">
        {badge && (
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase bg-white/10 border border-white/20 backdrop-blur-md shadow-xl"
            style={{ color: accentColor }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>{badge}</span>
          </div>
        )}

        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.15] drop-shadow-lg text-white">
          {title}
        </h1>

        <p className="text-base sm:text-lg text-slate-200 max-w-2xl mx-auto leading-relaxed drop-shadow font-medium">
          {description}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
          <WebsiteButton
            label={buttonText}
            action={buttonAction}
            accentColor={accentColor}
            size="lg"
            variant="primary"
            onNavigate={onNavigate}
            isEditor={!isPreview}
          />

          {secondaryButtonText && (
            <WebsiteButton
              label={secondaryButtonText}
              action={secondaryButtonAction}
              size="lg"
              variant="white"
              onNavigate={onNavigate}
              isEditor={!isPreview}
            />
          )}
        </div>
      </div>
    </section>
  );
};
